import React, { useState } from "react";
import PropTypes from "prop-types";
import { Form } from "formik";
import get from "lodash-es/get";
import set from "lodash-es/set";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";

// Formik
import * as Yup from "yup";

// Phone
import { isValidPhoneNumber } from "libphonenumber-js";

// Config
import styles from "src/config/styles";
import useTranslate from "src/hooks/useTranslate";

// Utils
import statements from "src/utils/statements";

// Components
import FormikHandler from "src/components/FormikHandler";
import DraftAutosave from "./DraftAutosave";
import FormContent from "./FormContent";
import FormReview from "./FormReview";

const useStyles = makeStyles({
  root: {
    ...styles.mixins.mainSection,
    display: "flex",
    flexDirection: "column",
    transition: "padding-top .3s ease-in-out"
  },
  paper: {
    border: "1px solid #EAEDF3",
    width: "100%"
  }
});

export const getDefaultValue = field => {
  switch (field.type) {
    case "boolean":
      return false;

    case "select":
      return field.validation?.enum?.[0] ?? "";

    case "date":
    case "dateTime":
    case "time":
      return null;

    default:
      return "";
  }
};

// "staticText" fields (type: "text" in the template) are read-only instructional
// content, not user input: they never render a Formik Field (see StatementInput),
// and the backend's validationSchema doesn't declare them either. Keeping them out
// of values/validation avoids submitting responses the backend rejects with
// "does not allow additional properties".
const isStaticField = field => field.type === "staticText";

// Field paths inside a "group" use dot-notation (e.g. "group_employment.employer"),
// matching how the backend's templateText/validationSchema reference them. Formik
// treats dots in a Field's name as a nested path, so initialValues/validation need
// the same nesting, not a flat object keyed by the literal dotted string.
//
// `responses` is whatever was last autosaved as a draft (see DraftAutosave):
// it's stored in the same nested shape as the values it was saved from, so a
// plain dotted-path `get` resumes the user's progress on top of the defaults.
export const getInitialValues = (schema, responses) => {
  const initialValues = {};

  Object.entries(schema.properties).forEach(([path, field]) => {
    if (isStaticField(field)) return;
    const savedValue = get(responses, path);
    set(
      initialValues,
      path,
      savedValue !== undefined
        ? statements.parseFieldValue(field, savedValue)
        : getDefaultValue(field)
    );
  });

  return initialValues;
};

const buildFieldValidator = (field, isRequired, texts) => {
  let validator;

  switch (field.type) {
    case "boolean":
      validator = Yup.boolean();
      break;

    case "date":
    case "time":
    case "dateTime":
      validator = Yup.string().nullable();
      break;

    default:
      validator = Yup.string();
      break;
  }

  if (field.isPhoneNumber) {
    // Validated against the raw, unnormalized value the user typed (e.g. a
    // bare national number), not the E.164 string it gets normalized to at
    // submit time (see formatFieldValue in src/utils/statements.js) - so the
    // backend's own pattern (built for the final E.164 shape) doesn't apply
    // here and would incorrectly reject valid input.
    validator = validator.test(
      "valid-phone",
      texts.phoneNumber.invalid,
      value => !value || isValidPhoneNumber(value, "GB")
    );
  } else if (field.validation?.pattern) {
    validator = validator.matches(
      new RegExp(field.validation.pattern),
      texts.invalidValue
    );
  }

  if (isRequired) {
    validator = validator.required(texts.required);
  }

  return validator;
};

export const getValidationSchema = (schema, texts) => {
  const tree = {};

  Object.entries(schema.properties).forEach(([path, field]) => {
    if (isStaticField(field)) return;

    const segments = path.split(".");
    const isRequired = schema.required?.includes(path) ?? false;
    let node = tree;

    segments.forEach((segment, index) => {
      if (index === segments.length - 1) {
        node[segment] = buildFieldValidator(field, isRequired, texts);
      } else {
        node[segment] = node[segment] ?? {};
        node = node[segment];
      }
    });
  });

  const toYupShape = node =>
    Yup.object().shape(
      Object.fromEntries(
        Object.entries(node).map(([key, value]) => [
          key,
          Yup.isSchema(value) ? value : toYupShape(value)
        ])
      )
    );

  return toYupShape(tree);
};

const StatementForm = ({ onComplete, schema, isSai, responses }) => {
  const classes = useStyles();
  const [isReviewing, setReviewing] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [maxPageIndex, setMaxPageIndex] = useState(0);
  const getText = useTranslate();

  // Lives here (not in FormContent) so it survives the Content <-> Review toggle:
  // "Edit" from Review lands back on the page the user was on, and Review's
  // breadcrumb can jump straight back into a specific page.
  const pages = schema.pages ?? [
    { fieldPaths: Object.keys(schema.properties) }
  ];

  const handleSubmit = () => {
    onComplete();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClickNext = () => {
    setReviewing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleGoBack = () => {
    setReviewing(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Forward moves (Next/Generate/breadcrumb) always validate first, so
  // `leavingPageValid` only ever goes false on a backward move: the page
  // being left was never re-checked before letting the user leave it, so any
  // page unlocked on top of it can no longer be trusted - maxPageIndex rolls
  // back to just before it, and the breadcrumb stops offering a shortcut past
  // it until it's fixed and re-validated via Next again.
  const goToPage = (index, { leavingPageValid = true } = {}) => {
    setPageIndex(index);
    setMaxPageIndex(current =>
      leavingPageValid
        ? Math.max(current, index)
        : Math.min(current, pageIndex - 1)
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNavigateToPage = index => {
    goToPage(index);
    setReviewing(false);
  };

  return (
    <Paper className={classes.paper} elevation={0}>
      <FormikHandler
        formik={{
          initialValues: getInitialValues(schema, responses),
          onSubmit: handleSubmit,
          validationSchema: getValidationSchema(
            schema,
            getText("common.validationSchema")
          )
        }}
        render={({ values, submitForm }) => (
          <Form
            className={classes.buttonContainer}
            onKeyDown={e => {
              const isTextarea = e.target instanceof HTMLTextAreaElement;
              if (!isReviewing && e.key === "Enter" && !isTextarea) {
                e.preventDefault();
              }
            }}
          >
            <DraftAutosave schema={schema} />
            {isReviewing ? (
              <FormReview
                isSai={isSai}
                values={values}
                schema={schema}
                pages={pages}
                pageIndex={pageIndex}
                maxPageIndex={maxPageIndex}
                onNavigateToPage={handleNavigateToPage}
                onSubmit={submitForm}
                onGoBack={handleGoBack}
              />
            ) : (
              <FormContent
                isSai={isSai}
                onContinue={handleClickNext}
                schema={schema}
                pages={pages}
                pageIndex={pageIndex}
                maxPageIndex={maxPageIndex}
                onNavigate={goToPage}
              />
            )}
          </Form>
        )}
      />
    </Paper>
  );
};

StatementForm.propTypes = {
  isSai: PropTypes.bool.isRequired,
  onComplete: PropTypes.func.isRequired,
  responses: PropTypes.shape({}),
  schema: PropTypes.shape({
    StatementText: PropTypes.string,
    properties: PropTypes.shape({}),
    pages: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        fieldPaths: PropTypes.arrayOf(PropTypes.string)
      })
    )
  }).isRequired
};

StatementForm.defaultProps = {
  responses: null
};

export default StatementForm;
